import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '../../hooks/useTheme';
import { Dialog, DialogTitle, IconButton } from '@mui/material';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import CloseIcon from '@mui/icons-material/Close';
// import { Download } from 'react-feather';
import DialogContent from '@mui/material/DialogContent';
import { CheckCircle, Download, XCircle } from 'lucide-react';
import axios from 'axios';
import host from '../../global';
import { toast } from 'react-toastify';
import {
  removeCandidateFromSelectedList,
  toggleSchedulePopupState,
} from '../../store/slices/candidateManagementSlice';

const getPathColor = (val) => {
  switch (true) {
    case val >= 60:
      return '#34B53A';
    case val >= 30 && val < 60:
      return '#FFB200';
    case val >= 0 && val < 30:
      return '#DC0000';
    default:
      return 'gray';
  }
};

const getScoreColorGradient = (score) => {
  switch (true) {
    case score >= 60:
      return 'rgba(0,255,0,0.2)';
    case score >= 30 && score < 60:
      return 'rgba(255, 255, 0, 0.2)';
    case score >= 0 && score < 30:
      return 'rgba(255, 0, 0, 0.2)';
    default:
      return 'text-[#CCCCCCE5]';
  }
};

const SkillBadge = ({ skill, status }) => {
  return (
    <div className="flex items-center gap-1.5">
      <span className="dark:text-gray-300 text-textPrimary">{skill}</span>
      {status === 'yes' ? (
        <CheckCircle className="w-4 h-4 text-green-500" />
      ) : (
        <XCircle className="w-4 h-4 text-red-500" />
      )}
    </div>
  );
};

function AnalysisReportModal({
  closeModal = () => {},
  handleScheduleInterview = () => {},
}) {
  const currentReport = useSelector(
    (state) => state.candidateManagementSlice.analysisReport,
  );
  const isReportModalOpen = useSelector(
    (state) => state.candidateManagementSlice.isReportModalOpen,
  );
  const dispatch = useDispatch();
  const accessToken = sessionStorage.getItem('accessToken');

  const currentCandidate = useSelector(
    (state) => state.candidateManagementSlice.currentCandidate,
  );



  //Download Resume
  const handleViewResume = async () => {
    if (currentCandidate.resume_s3_url) {
      try {
        const response = await axios.get(
          `${host}/download-resumes3?resume_s3_url=${encodeURIComponent(currentCandidate.resume_s3_url)}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            responseType: 'blob',
          },
        );

        const contentDisposition = response.headers['content-disposition'];
        const contentType =
          response.headers['content-type'] || 'application/octet-stream';

        let filename = `${currentReport.name}_resume.pdf`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
          );
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        } else {
          const candidateName = (currentReport.name || 'candidate')
            .replace(/[^a-zA-Z0-9\s]/g, '')
            .replace(/\s+/g, '_');
          filename = `${candidateName}_resume`;
        }

        let extension = 'pdf';
        if (contentType.includes('wordprocessingml')) extension = 'docx';
        else if (contentType.includes('msword')) extension = 'doc';
        else if (contentType.includes('pdf')) extension = 'pdf';

        if (!filename.endsWith(`.${extension}`)) {
          filename = `${filename}.${extension}`;
        }

        const blob = new Blob([response.data], { type: contentType });

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, filename);
        } else {
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filename;
          link.style.display = 'none';
          link.setAttribute('target', '_self');

          document.body.appendChild(link);

          setTimeout(() => {
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
          }, 100);
        }

        toast.success('Resume downloaded successfully');
      } catch (error) {
        console.error('Download error:', error);

        if (error.response?.status === 404) {
          const errorMessage =
            error.response?.data?.detail || 'Resume not found';
          toast.error(errorMessage);
        } else if (error.response?.status === 401) {
          toast.error('Unauthorized access. Please login again.');
        } else if (error.response?.status === 400) {
          const errorMessage =
            error.response?.data?.detail || 'Invalid request';
          toast.error(errorMessage);
        } else if (error.response?.status === 500) {
          toast.error('Server error. Please try again later.');
        } else {
          toast.error('Download failed. Please try again.');
        }
      }
    }
  };

  const { theme } = useTheme();
  return (
    <div className="w-full h-full flex flex-row items-center justify-center">
      <Dialog
        open={isReportModalOpen}
        onClose={()=>closeModal(true)}
        maxWidth="md"
        scroll="body"
        fullWidth
        PaperProps={
          theme === 'dark'
            ? {
                sx: {
                  background:
                    'linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))',
                  backdropFilter: 'blur(50px)',
                  WebkitBackdropFilter: 'blur(50px)',
                  maxHeight: 'none',
                  height: 'auto',
                },
              }
            : {
                className:
                  'bg-primary dark:bg-gray-900/50 rounded-3xl text-textPrimary',
              }
        }
      >
        {/* Analysis Report Dialog Box - Added Download Resume button */}
        <DialogTitle className="dark:text-[#8893C8] text-textPrimary text-sm flex justify-between items-center">
          <span>Analysis Report</span>

          <div className="flex items-center gap-2">
            {currentCandidate?.resume_s3_url ? (
              <button
                className="flex items-center gap-2 hover:opacity-80 transition-opacity pr-10"
                onClick={handleViewResume}
              >
                <Download className="w-4 h-4 dark:text-[#C9A3FF] text-textSecondary" />
                <span className="dark:text-[#C9A3FF] text-textSecondary font-normal text-sm">
                  Download Resume
                </span>
              </button>
            ) : (
              <span className="dark:text-gray-500 text-textSecondary text-xs italic">
                No Resume
              </span>
            )}

            <IconButton
              onClick={closeModal}
              size="small"
              sx={{
                color: '#8893C8',
                '&:hover': { color: 'white' },
                padding: '4px', // Optional: tighter spacing
              }}
            >
              <CloseIcon
                fontSize="small"
                className="dark:text-white text-textSecondary"
              />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent
          className=" dark:text-white text-textSecondary"
          sx={{ maxHeight: 'none', overflow: 'visible' }}
        >
          {currentReport && Object.keys(currentReport).length > 0 ? (
            <div className="w-full px-4 sm:px-0 pt-5">
              <div className="rounded-3xl dark:shadow-xl shadow-sm  p-4 sm:p-6 border border-gray-700/50">
                <div className="flex space-x-6">
                  <div className="h-36 w-36 relative">
                    <CircularProgressbar
                      value={
                        currentReport.score === null ||
                        currentReport.score === undefined ||
                        currentReport.score === 'NA' ||
                        currentReport.score === 'ERROR'
                          ? 0
                          : currentReport.score
                      }
                      text={
                        currentReport.score === null ||
                        currentReport.score === undefined ||
                        currentReport.score === 'NA' ||
                        currentReport.score === 'ERROR'
                          ? 'NA'
                          : `${currentReport.score}%`
                      }
                      circleRatio={0.75}
                      styles={buildStyles({
                        rotation: 1 / 2 + 1 / 8,
                        strokeLinecap: 'butt',
                        trailColor:
                          currentReport.score === null ||
                          currentReport.score === undefined ||
                          currentReport.score === 'NA' ||
                          currentReport.score === 'ERROR'
                            ? getScoreColorGradient(0)
                            : getScoreColorGradient(currentReport.score),
                        pathColor:
                          currentReport.score === null ||
                          currentReport.score === undefined ||
                          currentReport.score === 'NA' ||
                          currentReport.score === 'ERROR'
                            ? getPathColor(0)
                            : getPathColor(currentReport.score),
                        textColor: `${theme === 'dark' ? '#FFFFFF' : '#525252'}`,
                        textSize: '20px',
                      })}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        width: '100%',
                        textAlign: 'center',
                        // color: "#FFFFFF99",
                        fontSize: '14px',
                        fontWeight: 'normal',
                        bottom: '10px',
                      }}
                      className="dark:text-white text-textSecondary"
                    >
                      Score
                    </div>
                  </div>
                  <div className="flex-1 flex-col space-y-6 text-white pl-4">
                    <div className="flex-1 flex justify-between">
                      <div className="flex-1 flex flex-col border-r border-[#484474]">
                        <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold">
                          Experience Match
                        </span>
                        <div className="text-[#D0D5EF] mt-2">
                          {Array.isArray(currentReport.experience_match) &&
                          currentReport.experience_match.length > 0 ? (
                            currentReport.experience_match.map((exp, index) => (
                              <span
                                key={index}
                                style={{
                                  color:
                                    exp.meets_requirement &&
                                    exp.meets_requirement.toLowerCase() ===
                                      'yes'
                                      ? '#34B53A'
                                      : '#DC0000',
                                }}
                              >
                                {exp.candidate_experience}
                              </span>
                            ))
                          ) : (
                            <span className="dark:text-[#D0D5EF] text-textSecondary">
                              No experience data available
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-1 flex flex-col border-r border-[#484474] pl-5">
                        <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold">
                          Location
                        </span>
                        <span className="dark:text-[#D0D5EF] text-textSecondary mt-2">
                          {currentReport.location_match &&
                          Object.keys(currentReport.location_match).length > 0
                            ? `${
                                currentReport.location_match.candidate_location?.split(
                                  ',',
                                )[0] || 'Not specified'
                              }`
                            : 'No location data available'}
                        </span>
                      </div>
                      <div className="flex-1 flex flex-col pl-5">
                        <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold">
                          Education
                        </span>
                        <span className="dark:text-[#D0D5EF] text-textSecondary mt-2">
                          {currentReport.education_match &&
                          currentReport.education_match
                            .candidate_highest_education
                            ? currentReport.education_match
                                .candidate_highest_education
                            : 'No education data available'}
                        </span>
                      </div>
                    </div>
                    <div className="mt-5 border-t border-[#484474]"></div>
                    <div className="flex-1 flex justify-between">
                      <div className="flex flex-col border-r border-[#484474] w-[32%]">
                        <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold">
                          Red Flags
                        </span>
                        <span className="dark:text-[#D0D5EF] text-textSecondary mt-2">
                          {Array.isArray(currentReport.red_flags) &&
                          currentReport.red_flags.length > 0
                            ? currentReport.red_flags.join(', ')
                            : 'None'}
                        </span>
                      </div>
                      <div className="flex-1 flex flex-col border-[#484474] pl-5">
                        <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold">
                          Tech/Tools Match
                        </span>
                        <div className="mt-2">
                          {Array.isArray(currentReport.tech_tool_match) &&
                          currentReport.tech_tool_match.length > 0 ? (
                            <div className="flex flex-wrap gap-3">
                              {[...currentReport.tech_tool_match]
                                .sort((a, b) => {
                                  const aStatus =
                                    a.skill_status?.toLowerCase() === 'yes';
                                  const bStatus =
                                    b.skill_status?.toLowerCase() === 'yes';
                                  if (aStatus && !bStatus) return -1;
                                  if (!aStatus && bStatus) return 1;
                                  return 0;
                                })
                                .map((skill, index) => (
                                  <SkillBadge
                                    key={index}
                                    skill={skill.skill_name}
                                    status={
                                      skill.skill_status?.toLowerCase() ===
                                      'yes'
                                        ? 'yes'
                                        : 'no'
                                    }
                                  />
                                ))}
                            </div>
                          ) : (
                            <span className="dark:text-[#D0D5EF] text-textSecondary">
                              No tech/tools data available
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 border-t border-[#484474]">
                  <div className="flex justify-between mt-5">
                    <div className="flex-1 flex flex-col border-r border-[#484474] pr-5">
                      <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold">
                        Highlights
                      </span>
                      <span className="dark:text-[#D0D5EF] text-textSecondary mt-2">
                        {Array.isArray(currentReport.Strengths) &&
                        currentReport.Strengths.length > 0
                          ? currentReport.Strengths.map((point) =>
                              point.replace(/^- /, '').trim(),
                            ).join(' ')
                          : 'No highlights available'}
                      </span>
                    </div>
                    <div className="flex-1 flex flex-col border-[#484474] pl-5">
                      <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold">
                        Lowlights
                      </span>
                      <span className="dark:text-[#D0D5EF] text-textSecondary mt-2">
                        {Array.isArray(currentReport.Weaknesses) &&
                        currentReport.Weaknesses.length > 0
                          ? currentReport.Weaknesses.map((point) =>
                              point.replace(/^- /, '').trim(),
                            ).join(' ')
                          : 'No lowlights available'}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col mt-5 border-t border-[#484474]">
                    <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold mt-5">
                      Suggestions
                    </span>
                    <span className="dark:text-[#D0D5EF] text-textSecondary mt-2">
                      {Array.isArray(currentReport.Suggestions) &&
                      currentReport.Suggestions.length > 0
                        ? currentReport.Suggestions.map((point) =>
                            point.replace(/^- /, '').trim(),
                          ).join(' ')
                        : 'No suggestions available'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="dark:text-[#D0D5EF] text-textSecondary">
              No report available
            </p>
          )}
          {currentCandidate && (
            <div className="flex justify-end gap-3 mt-6 px-2 pb-2">
              <button
                className={
                  theme === 'dark'
                    ? 'button text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed'
                    : 'border-btn hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed'
                }
                onClick={() => {
                  closeModal(); // Close the analysis report modal
                }}
              >
                <span className="dark:text-white text-textSecondary">
                  Close
                </span>
              </button>
              <button
                className="px-4 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-sm button hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  console.log(
                    ' setSelectedCandidates([currentCandidate]); & setIsSchedulePopup(true) Clicked',
                  );
                  handleScheduleInterview(currentCandidate);
                  closeModal(false); // Close the analysis report modal
                }}
                disabled={
                  currentCandidate?.interview_id &&
                  currentCandidate?.status === 'scheduled'
                }
              >
                <span className="text-white">Schedule Interview</span>
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AnalysisReportModal;
